import { createQueryKeySet } from '~/utils/query-keys';
import { useQuery } from '@tanstack/react-query';
import { SSF } from '~/apis/api';

export const keys = createQueryKeySet<ClubListQueryParams>('clubs');

export type ClubListQueryParams = Parameters<typeof SSF.api.clubsList>[0];
export type ClubListOrdering = Exclude<ClubListQueryParams, undefined>['ordering'];

export function useClubListQuery(params?: ClubListQueryParams, enabled = true) {
  return useQuery({
    queryFn: async () => {
      const { data } = await SSF.api.clubsList(params);
      return data;
    },
    queryKey: keys.list(params),
    enabled,
  });
}

/**
 * Fetches every club, following pagination since the API caps page_size at 100.
 * Intended for pickers that need the complete club list up front.
 */
export function useAllClubsQuery() {
  const params = { ordering: 'name', page_size: 100 } satisfies ClubListQueryParams;

  return useQuery({
    queryFn: async () => {
      const { data: first } = await SSF.api.clubsList({ ...params, page: 1 });
      const pages = Array.from({ length: first.page_count - 1 }, (_, i) => i + 2);
      const rest = await Promise.all(pages.map(page => SSF.api.clubsList({ ...params, page })));

      return [first, ...rest.map(({ data }) => data)].flatMap(page => page.results);
    },
    queryKey: [...keys.lists(), 'all'],
    staleTime: Infinity,
  });
}

export function useClubQuery(id: number) {
  return useQuery({
    queryFn: async () => {
      const { data } = await SSF.api.clubsRetrieve(id);
      return data;
    },
    queryKey: keys.detail(id),
  });
}
